// @file Surface map store
import { dir } from '@@/bits/current_dir'
import device from '@@/bits/device'
import { asciiSafeStringify } from '@@/bits/json_stringify'
import type { LatLng, OverlappingMarkerSpiderfier } from '@@/bits/map'
import { getVuexStore } from '@@/bits/pinia'
import { setCustomPropertyValue } from '@@/bits/post_properties'
import { safeLocalStorage } from '@@/bits/safe_storage'
import { useGlobalSnackbarStore } from '@@/pinia/global_snackbar'
import { usePostComposerModalStore } from '@@/pinia/post_composer_modal_store'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfaceContainerSizeStore } from '@@/pinia/surface_container_size'
import { useSurfaceDraftsStore } from '@@/pinia/surface_drafts'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import { useSurfaceShareLinksStore } from '@@/pinia/surface_share_links'
import { useWindowSizeStore } from '@@/pinia/window_size'
import type {
  Cid,
  DraftPost,
  Id,
  LocationMenuPlaceInfo,
  PinDropSuggestion,
  PostColor,
  PostCustomProperties,
} from '@@/types'
import type { RootState } from '@@/vuexstore/surface/types'
import type { JsonAPIResource, JsonAPIResponse, WallTheme } from '@padlet/arvo'
import { fetchMapThemes as arvoFetchMapThemes } from '@padlet/arvo'
import { cloneDeep } from 'es-toolkit'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

interface PostLocation {
  postCid: Cid | null
  latLng: LatLng | null
  name: string
  sectionId?: Id
}

export interface MapPopupOffsetStyle {
  top: number
  bottom: number
  left: number
  right: number
}

export const useSurfaceMapStore = defineStore('surfaceMap', () => {
  const surfaceStore = useSurfaceStore()
  const surfaceContainerSizeStore = useSurfaceContainerSizeStore()
  const surfacePostsStore = useSurfacePostsStore()
  const surfaceDraftsStore = useSurfaceDraftsStore()
  const windowSizeStore = useWindowSizeStore()
  const globalSnackbarStore = useGlobalSnackbarStore()
  const surfaceShareLinksStore = useSurfaceShareLinksStore()

  /* --------------------------------------------- */
  /* FETCH & SHOW MAP THEME                        */
  /* --------------------------------------------- */
  const map = ref<google.maps.Map | null | undefined>(null)
  const mapThemes = ref<Record<number, WallTheme>>()
  const isFetchingMapThemes = ref(false)
  const currentThemeId = computed<number>(() => getVuexStore<RootState>()?.getters.themeId)
  const currentMapTheme = computed(() =>
    mapThemes.value !== undefined ? mapThemes.value[currentThemeId.value] : undefined,
  )
  const isMapThemeChanged = computed<boolean>(
    () => getVuexStore<RootState>()?.getters.wall.theme_id !== surfaceStore.mapThemeId,
  )

  const setMapBeingUsed = ({ map: updatedMap }: { map: google.maps.Map }): void => {
    map.value = updatedMap
  }

  // We don't show the number of overlapping markers in the spider cluster for PeopleoverPapers
  // because the maps in this library have too many markers, and the calculation of the number of overlapping markers
  // is too CPU intensive.
  const showMarkerOverlapCount = computed<boolean>(() => {
    // Do not show count if there are more than 50 markers since it is very CPU intensive
    if (surfacePostsStore.currentPostsCount > 50) return false
    return surfaceStore.libraryId?.toString() !== '330024'
  })

  async function fetchMapThemes(): Promise<void> {
    isFetchingMapThemes.value = true
    const themesResponse: JsonAPIResponse<WallTheme> = await arvoFetchMapThemes()
    if (themesResponse.data != null) {
      const mapThemeDictionary = {}
      const allThemes = themesResponse.data as Array<JsonAPIResource<WallTheme>>
      allThemes.forEach((mapTheme: JsonAPIResource<WallTheme>): void => {
        const details = mapTheme.attributes
        mapThemeDictionary[details.id] = details
      })
      mapThemes.value = mapThemeDictionary
    }
    isFetchingMapThemes.value = false
  }

  /* --------------------------------------------- */
  /* PICKING LOCATIONS                             */
  /* --------------------------------------------- */
  const isPickingLocation = ref<boolean>(false)
  const postWithLocationBeingEditedCid = ref<Cid | null>(null)
  const sectionIdOfPostBeingAdded = ref<Id | null>(null)

  const hidePickLocationPanel = (): void => {
    isPickingLocation.value = false
  }

  function startPickingLocation(payload?: {
    postCid?: Cid
    sectionId?: Id
    color?: PostColor
    custom_properties?: PostCustomProperties | null
  }): void {
    // On breakout links we enforce/predefine attributes on any new post.
    if (surfaceShareLinksStore.isColorBreakout) {
      payload = {
        ...payload,
        color: surfaceShareLinksStore.colorBreakoutPostColor,
      }
    }

    if (
      surfaceShareLinksStore.isSingleSelectBreakout &&
      surfaceShareLinksStore.singleSelectBreakoutProperty &&
      surfaceShareLinksStore.singleSelectBreakoutOptionId
    ) {
      payload = setCustomPropertyValue(
        payload ?? {},
        surfaceShareLinksStore.singleSelectBreakoutProperty,
        surfaceShareLinksStore.singleSelectBreakoutOptionId,
      )
    }

    // Show the post composer when not in app
    if (!device.app) {
      void surfacePostsStore.startNewPost({
        attributes: {
          wall_section_id: payload?.sectionId,
          color: payload?.color,
          custom_properties: payload?.custom_properties,
        },
      })
    } else {
      // TODO: Handle color and custom properties payload support in mobile app
      isPickingLocation.value = true
      postWithPopupBeingOpenedCid.value = null
      sectionIdOfPostBeingAdded.value = payload?.sectionId as number
      if (payload?.postCid != null) {
        postWithLocationBeingEditedCid.value = payload.postCid
      }
    }
  }

  function startPickingNewLocation(payload: { postCid: Cid }): void {
    postWithLocationBeingEditedCid.value = payload.postCid
  }
  function stopPickingNewLocation(): void {
    postWithLocationBeingEditedCid.value = null
  }

  const stopPickingLocation = (): void => {
    isPickingLocation.value = false
    postWithLocationBeingEditedCid.value = null
  }

  const pickLocation = (payload: Partial<PostLocation>): void => {
    if (!device.app) {
      if (payload.latLng == null) {
        globalSnackbarStore.genericFetchError()
        throw new Error("Location's coordinates not found")
      }
      if (postWithLocationBeingEditedCid.value !== null) {
        pickLocationForPostBeingEdited(payload.latLng, payload.name ?? '')
      } else if (!isPlacingPin.value) {
        pickLocationForDraftBeingEdited(payload.latLng, payload.name ?? '')
      } else {
        pickLocationForDraftBeingEditedWithPlacedPin(payload.latLng, payload.name ?? '')
      }
    } else {
      if (!payload.postCid && !postWithLocationBeingEditedCid.value) {
        pickLocationAndCreateNewPost(payload as PostLocation)
      } else {
        pickLocationAndUpdateLocation(payload as PostLocation)
      }
    }
  }

  function pickLocationForPostBeingEdited(latLng: LatLng, name: string): void {
    if (postWithLocationBeingEditedCid.value === null) {
      globalSnackbarStore.genericFetchError()
      throw new Error('No post found')
    }
    surfacePostsStore.startEditingPost({ postCid: postWithLocationBeingEditedCid.value }) // Reopen the post that was being edited
    stopPickingNewLocation() // // Set postWithLocationBeingEditedCid to null
    if (surfaceDraftsStore.activeDraftCid == null) {
      globalSnackbarStore.genericFetchError()
      throw new Error('No active draft found')
    }
    updateDraftLocation(surfaceDraftsStore.activeDraftCid, latLng, name)
    usePostComposerModalStore().restoreComposerModalState(surfaceDraftsStore.activeDraftCid ?? '')
  }

  function pickLocationForDraftBeingEdited(latLng: LatLng, name: string): void {
    if (surfaceDraftsStore.activeDraftCid == null) {
      globalSnackbarStore.genericFetchError()
      throw new Error('No active draft found')
    }
    updateDraftLocation(surfaceDraftsStore.activeDraftCid, latLng, name)
    usePostComposerModalStore().restoreComposerModalState(surfaceDraftsStore.activeDraftCid ?? '')
  }

  function pickLocationForDraftBeingEditedWithPlacedPin(latLng: LatLng, name: string): void {
    showDraftBeingEdited()
    draftWithLocationBeingEditedCid.value = null
    if (surfaceDraftsStore.activeDraftCid == null) {
      globalSnackbarStore.genericFetchError()
      throw new Error('No active draft found')
    }
    updateDraftLocation(surfaceDraftsStore.activeDraftCid, latLng, name)
    isPlacingPin.value = false
  }

  function showDraftBeingEdited(): void {
    if (draftWithLocationBeingEditedCid.value == null) {
      globalSnackbarStore.genericFetchError()
      throw new Error('No draft found')
    }
    if (surfaceContainerSizeStore.isPhone || surfacePostsStore.isExistingPost(draftWithLocationBeingEditedCid.value)) {
      // On phone, set activeDraftCid to draftWithLocationBeingEditedCid to open the draft's composer modal
      // If the draft is an existing post, setting activeDraftCide to draftWithLocationBeingEditedCid will open the post's composer modal
      surfaceDraftsStore.startEditingDraft(draftWithLocationBeingEditedCid.value)
    } else {
      // On tablet and other touchable devices, expandComposerModal to open the draft's composer modal
      usePostComposerModalStore().expandComposerModal(draftWithLocationBeingEditedCid.value)
    }
  }

  function updateDraftLocation(cid: Cid, latLng: LatLng, name: string): void {
    const updateData: Partial<DraftPost> = {
      cid,
      location_point: {
        longitude: latLng?.lng(),
        latitude: latLng?.lat(),
      },
      location_name: name,
    }

    const isSubjectWrittenByUser: boolean =
      surfaceDraftsStore.activeDraft?.subject !== surfaceDraftsStore.activeDraft?.location_name &&
      surfaceDraftsStore.activeDraft?.subject !== ''
    if (surfaceStore.isMap && !isSubjectWrittenByUser) {
      updateData.subject = name
    }

    surfaceDraftsStore.updateDraft(updateData)
  }

  const pickLocationAndCreateNewPost = (payload: Omit<PostLocation, 'postCid'>): void => {
    surfacePostsStore.startNewPost({
      attributes: {
        locationPoint: {
          longitude: payload.latLng?.lng() || 0,
          latitude: payload.latLng?.lat() || 0,
        },
        subject: payload.name,
        locationName: payload.name,
        wall_section_id: sectionIdOfPostBeingAdded.value as number,
      },
    })
    sectionIdOfPostBeingAdded.value = null

    // Delay hiding the panel to offload the frontend animation
    // Map have to fit, pan, create marker, create post.
    setTimeout((): void => {
      stopPickingLocation()
    }, 500)
  }

  const pickLocationAndUpdateLocation = (payload: PostLocation): void => {
    const postToChangeCid = payload.postCid || postWithLocationBeingEditedCid.value
    if (postToChangeCid) {
      const postWithLocationBeingEdited = surfacePostsStore.postEntitiesByCid[postToChangeCid]
      const newLocationPoint = payload.latLng
        ? {
            longitude: payload.latLng.lng(),
            latitude: payload.latLng.lat(),
          }
        : postWithLocationBeingEdited.location_point
      const newAttributes = {
        ...postWithLocationBeingEdited,
        location_point: newLocationPoint,
        location_name: payload.name,
      }
      if (postWithLocationBeingEdited.location_name === postWithLocationBeingEdited.subject) {
        newAttributes.subject = payload.name
      }
      surfacePostsStore.savePost({ attributes: newAttributes })
    }
    // Delay hiding the panel to offload the frontend animation
    // Map have to fit, pan, create marker, create post.
    setTimeout((): void => {
      stopPickingLocation()
    }, 500)
  }

  /* --------------------------------------------- */
  /* SHOW MAP POST, MAKRER AND PREVIEW PANEL       */
  /* --------------------------------------------- */
  const mapDoesFitMarkersBounds = ref<boolean>(false)
  const pinDropSuggestions = ref<PinDropSuggestion[] | null>(null)
  const pinDropLocation = ref<google.maps.LatLng | null>(null)
  const postWithPopupBeingOpenedCid = ref<Cid | null>(null)
  const postWithMarkerHighlightedCid = ref<Cid | null>(null)
  const overlappingMarkerSpiderfier = ref<OverlappingMarkerSpiderfier | null>(null)
  const isPreviewPanelShown = ref<boolean>(true)

  const setMarkerOverlayLayer = (payload: { spiderfier: OverlappingMarkerSpiderfier }): void => {
    overlappingMarkerSpiderfier.value = payload.spiderfier
  }

  function openPostPopup(payload: { postCid: Cid | null }): void {
    postWithPopupBeingOpenedCid.value = payload.postCid
  }

  const closePostPopup = (): void => {
    postWithPopupBeingOpenedCid.value = null
  }

  const highlightPostMarker = (payload: { postCid: Cid | null }): void => {
    postWithMarkerHighlightedCid.value = payload.postCid
  }

  const toggleMapPreviewPanel = (): void => {
    isPreviewPanelShown.value = !isPreviewPanelShown.value
  }

  const enableMapFittingMarkersBounds = (): void => {
    mapDoesFitMarkersBounds.value = true
  }

  const disableMapFittingMarkersBounds = (): void => {
    mapDoesFitMarkersBounds.value = false
  }

  const setMapPinDropSuggestions = ({ suggestions, location }): void => {
    pinDropLocation.value = location
    pinDropSuggestions.value = suggestions
  }

  const clearMapPinDropSuggestions = (): void => {
    pinDropLocation.value = null
    pinDropSuggestions.value = null
  }

  /* --------------------------------------------- */
  /* MAP RELATED POSITIONING                       */
  /* --------------------------------------------- */
  const offsetForMapPopup = computed<MapPopupOffsetStyle>(() => {
    // We are still using the old layout in embedded padlets, so we need to use the old measures.
    if (surfaceStore.isEmbedded) {
      return {
        top: surfaceStore.headerHeight + 24 || 160, // space for the surface header + post action menu
        bottom: device.app ? 160 : 80, // enough space for the marker and the popup tip
        left: 20, // little empty space on the left
        right: 60, // space on the right for the controls: FAB, zoom and reset viewport
      }
    }

    const desktopSidebarWidth = 72
    const desktopMapControlHeight = 32 + 20 // 32px for the map control, 20px for its bottom margin
    const mobileBottomBarHeight = 52
    const mobilePostListHeight = 100 + 8 // 8px for its bottom margin
    const mobileMapControlWidth = 32 + 16 // 32px for the map control, 16px for its left margin
    const toolTipAndMarkerHeight = 60
    const padding = 24 // additional padding so the popup won't touch the edges of the map
    const isRtl = dir() === 'rtl'

    const baseOffset = {
      top: surfaceStore.headerHeight + padding,
      bottom: device.app
        ? mobilePostListHeight + toolTipAndMarkerHeight + padding
        : surfaceStore.isMobileLayout
          ? toolTipAndMarkerHeight + padding - mobileBottomBarHeight / 2 - mobilePostListHeight / 2
          : desktopMapControlHeight + toolTipAndMarkerHeight + padding,
    }

    const startOffset = windowSizeStore.isSmallerThanTabletPortrait ? mobileMapControlWidth + padding : padding

    const endOffset = windowSizeStore.isSmallerThanTabletPortrait ? padding : desktopSidebarWidth + padding

    return isRtl
      ? {
          ...baseOffset,
          left: endOffset,
          right: startOffset,
        }
      : {
          ...baseOffset,
          left: startOffset,
          right: endOffset,
        }
  })

  const postListPosition = computed<'side' | 'bottom'>(() =>
    surfaceStore.isMobileLayout || device.app || surfaceStore.isEmbedded ? 'bottom' : 'side',
  )
  /* --------------------------------------------- */
  /* SAFE STORAGE BASED RECENT SEARCHES            */
  /* --------------------------------------------- */
  const recentLocationMenuSearches = ref<LocationMenuPlaceInfo[]>([])

  const reversedRecentLocationMenuSearches = computed<LocationMenuPlaceInfo[]>(() =>
    [...recentLocationMenuSearches.value].reverse(),
  )

  const saveRecentlySearchedLocations = (payload: { newPlace: LocationMenuPlaceInfo }): void => {
    const rawValue = safeLocalStorage.getItem('locationMenuRecentSearches')
    const currentPlaces: LocationMenuPlaceInfo[] = rawValue ? JSON.parse(rawValue) : []

    let newPlaces = currentPlaces.filter((place: LocationMenuPlaceInfo) => place.id !== payload.newPlace.id)

    newPlaces.push(payload.newPlace)
    newPlaces = newPlaces.slice(-10)

    safeLocalStorage.setItem('locationMenuRecentSearches', asciiSafeStringify(newPlaces))

    // need cloneDeep because newLocations is an array of objects
    recentLocationMenuSearches.value = cloneDeep(newPlaces)
  }

  const syncRecentlySearchedLocationsFromStorage = (): void => {
    const rawValue = safeLocalStorage.getItem('locationMenuRecentSearches')
    const places: LocationMenuPlaceInfo[] = rawValue ? JSON.parse(rawValue) : []
    recentLocationMenuSearches.value = cloneDeep(places)
  }

  /* --------------------------------------------- */
  /* DRAG & DROP                                   */
  /* --------------------------------------------- */
  const isDraggingPosts = ref<boolean>(false)
  const isDraggingLocationPickerPin = ref<boolean>(false)

  function startDraggingPosts(): void {
    isDraggingPosts.value = true
  }

  function stopDraggingPosts(): void {
    isDraggingPosts.value = false
  }

  const startDraggingLocationPickerPin = (): void => {
    isDraggingLocationPickerPin.value = true
  }

  const stopDraggingLocationPickerPin = (): void => {
    isDraggingLocationPickerPin.value = false
  }

  /* --------------------------------------------- */
  /* MAP LOCATION SELECTOR                         */
  /* --------------------------------------------- */

  const xMapLocationSelector = computed<boolean>(
    () =>
      surfaceStore.isMap &&
      surfaceDraftsStore.activeDraft != null &&
      surfaceDraftsStore.activeDraft.location_point == null &&
      !hasDraftSkippedLocationSelector(surfaceDraftsStore.activeDraftCid as Cid),
  )

  const draftsThatHaveSkippedLocationSelector = ref<Set<Cid>>(new Set())
  function skipLocationSelectorForDraft(draftCid: Cid): void {
    draftsThatHaveSkippedLocationSelector.value.add(draftCid)
  }
  function hasDraftSkippedLocationSelector(draftCid: Cid): boolean {
    return draftsThatHaveSkippedLocationSelector.value.has(draftCid)
  }

  /* --------------------------------------------- */
  /* PLACING PIN                                   */
  /* --------------------------------------------- */

  const isPlacingPin = ref<boolean>(false)
  const setIsPlacingPin = (value: boolean): void => {
    isPlacingPin.value = value
  }

  const placedPinSuggestions = ref<PinDropSuggestion[] | null>(null)
  function setPlacedPinSuggestions(suggestions: PinDropSuggestion[], location: LatLng): void {
    placedPinSuggestions.value = suggestions
  }

  function clearPlacedPinSuggestions(): void {
    placedPinSuggestions.value = null
  }

  // A draft's location can be edited only when a pin is being placed to set its location on mobile
  const draftWithLocationBeingEditedCid = ref<Cid | null>(null)
  function setDraftWithLocationBeingEditedCid(cid: Cid | null): void {
    draftWithLocationBeingEditedCid.value = cid
  }
  return {
    // state
    mapThemes,
    isFetchingMapThemes,
    currentThemeId,
    isMapThemeChanged,
    currentMapTheme,
    postListPosition,
    mapDoesFitMarkersBounds,
    map,
    pinDropSuggestions,
    pinDropLocation,
    isPickingLocation,
    postWithPopupBeingOpenedCid,
    postWithLocationBeingEditedCid,
    postWithMarkerHighlightedCid,
    sectionIdOfPostBeingAdded,
    overlappingMarkerSpiderfier,
    offsetForMapPopup,
    isPreviewPanelShown,
    recentLocationMenuSearches: reversedRecentLocationMenuSearches,
    isDraggingLocationPickerPin,
    isDraggingPosts,
    xMapLocationSelector,
    isPlacingPin,
    placedPinSuggestions,
    draftWithLocationBeingEditedCid,
    showMarkerOverlapCount,

    // actions
    fetchMapThemes,
    startDraggingPosts,
    stopDraggingPosts,
    enableMapFittingMarkersBounds,
    disableMapFittingMarkersBounds,
    setMapBeingUsed,
    setMapPinDropSuggestions,
    clearMapPinDropSuggestions,
    hidePickLocationPanel,
    startPickingLocation,
    stopPickingLocation,
    pickLocation,
    pickLocationAndCreateNewPost,
    pickLocationAndUpdateLocation,
    setMarkerOverlayLayer,
    openPostPopup,
    closePostPopup,
    highlightPostMarker,
    toggleMapPreviewPanel,
    saveRecentlySearchedLocations,
    syncRecentlySearchedLocationsFromStorage,
    startDraggingLocationPickerPin,
    stopDraggingLocationPickerPin,
    startPickingNewLocation,
    stopPickingNewLocation,
    skipLocationSelectorForDraft,
    setIsPlacingPin,
    setPlacedPinSuggestions,
    clearPlacedPinSuggestions,
    setDraftWithLocationBeingEditedCid,
  }
})
